/**
 * Dev-only helpers for proxying tile requests from a Vite dev server to a remote tile server.
 *
 * A `localhost` cookie is never sent to `*.civilgrid.com`, so local pages reach tiles through a
 * same-origin dev proxy. The proxy runs in Node, where the HttpOnly cookie is an ordinary request
 * header, and converts it to a Bearer token — priority 1 in the tile server's `getToken`.
 */

/** Structural stand-ins for http-proxy's types, so this module needs no vite/http-proxy dependency. */
type ProxyRequest = {
  getHeader(name: string): unknown;
  setHeader(name: string, value: string): void;
  removeHeader(name: string): void;
};
type IncomingRequest = { headers: { cookie?: string } };
type ProxyEmitter = {
  on(event: 'proxyReq', listener: (proxyReq: ProxyRequest, req: IncomingRequest) => void): void;
};

/** Reads one cookie value out of a raw `Cookie` header. Exact name match, so `a.b` never matches `x.a.b`. */
export function readCookie(cookieHeader: string | undefined, name: string): string | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(';')) {
    const raw = part.trim();
    const eq = raw.indexOf('=');
    if (eq > 0 && raw.slice(0, eq) === name) return raw.slice(eq + 1);
  }
  return null;
}

/**
 * Builds a Vite `server.proxy` `configure` hook that turns the local auth cookie into a Bearer
 * header. The cookie is dropped because each environment names it differently, so forwarding it
 * would never match the upstream's configured name.
 */
export function tileProxyConfigure(cookieNames: string[]) {
  return (proxy: ProxyEmitter) => {
    proxy.on('proxyReq', (proxyReq, req) => {
      if (!proxyReq.getHeader('authorization')) {
        for (const name of cookieNames) {
          const jwt = readCookie(req.headers.cookie, name);
          if (jwt) {
            proxyReq.setHeader('authorization', `Bearer ${jwt}`);
            break;
          }
        }
      }
      proxyReq.removeHeader('cookie');
    });
  };
}

/**
 * Rebases an absolute tile URL onto `tileServerBase`. The tile server stamps its own base into the
 * tile URLs it returns, so a local client must redirect them at its dev proxy (or a local tile
 * server) — a cross-origin request to the real host would never reach the proxy. Dev-only: callers
 * gate on their environment so deployed builds use the URLs exactly as returned.
 */
export function rebaseTileUrl(url: string, tileServerBase: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!parsed.pathname.startsWith('/v1/tiles')) return url;
  // decodeURI restores mapbox `{z}/{x}/{y}` placeholders that URL parsing percent-encodes.
  return decodeURI(`${tileServerBase.replace(/\/$/, '')}${parsed.pathname}${parsed.search}`);
}

/**
 * Builds a dev `tileServerBase` on the page's own origin so the HttpOnly cookie is attached.
 * Reads `location` off `globalThis` rather than `window`: client tests run in a node environment
 * and stub `window` as a bare object, and `utils` is also consumed by backends with no DOM types.
 */
export function devTileServerBase(fallbackOrigin: string, path = '/tiles-proxy'): string {
  const origin = (globalThis as { location?: { origin?: string } }).location?.origin;
  return `${origin || fallbackOrigin}${path}`;
}
